import React, { useState } from 'react';
import { Benachrichtigungen } from '../components/portal/Benachrichtigungen';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { CodeEingeben } from '../components/MandantKontext';
import { ZweiFaktor } from '../components/ZweiFaktor';
import { Anmeldewege } from '../components/Anmeldewege';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail, Calendar, LogOut, User as UserIcon, BadgeCheck, Lock, Check, AlertCircle, Loader2, Compass } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PasswordChange = () => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) { setError(t('profil.password_change.error_min_length')); return; }
    if (next !== confirm) { setError(t('profil.password_change.error_mismatch')); return; }
    setState('loading');
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true });
      setState('success');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      setState('idle');
      setError(err?.response?.data?.detail || t('profil.password_change.error_failed'));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3 mt-6 p-5 bg-white border border-gray-200" data-testid="pw-change-form">
      <h3 className="text-[#1B3A5C] text-xs tracking-[0.2em] font-semibold mb-3 flex items-center gap-2">
        <Lock className="w-3.5 h-3.5 text-[#b8893d]" /> {t('profil.password_change.heading')}
      </h3>
      <input type="password" required placeholder={t('profil.password_change.placeholder_current')} value={current}
             onChange={(e) => setCurrent(e.target.value)} data-testid="pw-current"
             className="w-full px-3 py-2 text-sm border border-gray-300" />
      <input type="password" required minLength={8} placeholder={t('profil.password_change.placeholder_new')} value={next}
             onChange={(e) => setNext(e.target.value)} data-testid="pw-new"
             className="w-full px-3 py-2 text-sm border border-gray-300" />
      <input type="password" required placeholder={t('profil.password_change.placeholder_confirm')} value={confirm}
             onChange={(e) => setConfirm(e.target.value)} data-testid="pw-confirm"
             className="w-full px-3 py-2 text-sm border border-gray-300" />
      {error && (
        <div className="flex items-center gap-1.5 text-red-700 text-xs bg-red-50 px-2 py-1.5">
          <AlertCircle className="w-3.5 h-3.5" /> {error}
        </div>
      )}
      {state === 'success' && (
        <div className="flex items-center gap-1.5 text-emerald-700 text-xs bg-emerald-50 px-2 py-1.5">
          <Check className="w-3.5 h-3.5" /> {t('profil.password_change.success')}
        </div>
      )}
      <Button type="submit" disabled={state === 'loading'} data-testid="pw-submit"
              className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-xs">
        {state === 'loading' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : null}
        {t('profil.password_change.submit')}
      </Button>
    </form>
  );
};

const Profil = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const initials = (user?.name || 'M')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-[150px] pb-20 bg-[#f5f8fa] min-h-[80vh]">
        <section className="py-10">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            {!window.location.pathname.startsWith('/kanzlei/') && <Link to="/dashboard" className="inline-flex items-center gap-1 text-[#34536b] text-sm hover:text-[#b8893d] mb-4">
              <ArrowLeft className="w-3.5 h-3.5" /> {t('profil.back_to_dashboard')}
            </Link>}
            <h1 className="font-serif text-3xl md:text-4xl text-[#1B3A5C] mb-8">{t('profil.page_title')}</h1>
            {(new URLSearchParams(window.location.search).get('zwei_fa') === 'pflicht' || (['admin', 'kanzlei_mitarbeiter', 'freelancer'].includes(user?.role) && !user?.zwei_fa_aktiv)) && (
              <div className="mb-6 p-4 border-l-4 border-[#b8893d] bg-[#fbf7f0] text-sm text-[#1B3A5C]" data-testid="zwei-fa-pflicht-hinweis">
                <b>Zwei-Faktor-Anmeldung ist für Kanzlei-Konten Pflicht.</b> Bitte richten Sie sie unten unter „Zwei-Faktor-Anmeldung“ ein – erst danach ist der Kanzlei-Bereich nutzbar.
              </div>
            )}

            <div className="bg-white rounded-sm shadow-sm border-l-4 border-[#b8893d] overflow-hidden">
              <div className="bg-[#1B3A5C] text-white p-6 flex items-center gap-5">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full border-2 border-[#b8893d] object-cover" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-[#b8893d] flex items-center justify-center text-white font-serif text-3xl">
                    {initials}
                  </div>
                )}
                <div>
                  <h2 className="font-serif text-2xl">{user?.name}</h2>
                  <p className="text-white/70 text-sm">{user?.email}</p>
                  {user?.role === 'admin' && (
                    <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-[#b8893d] text-white text-[10px] tracking-wider rounded-sm">
                      <BadgeCheck className="w-3 h-3" /> {t('profil.role_admin')}
                    </span>
                  )}
                </div>
              </div>

              <div className="p-6 space-y-4">
                <h3 className="text-[#1B3A5C] text-xs tracking-[0.2em] font-semibold mb-3">{t('profil.account_details.heading')}</h3>
                {[
                  { icon: UserIcon, label: t('profil.account_details.name'), value: user?.name },
                  { icon: Mail, label: t('profil.account_details.email'), value: user?.email },
                  { icon: Calendar, label: t('profil.account_details.member_since'), value: user?.created_at ? new Date(user.created_at).toLocaleDateString('de-DE') : '—' },
                ].map((row) => (
                  <div key={row.label} className="flex items-center gap-4 py-3 border-b border-gray-100 last:border-0">
                    <div className="w-10 h-10 bg-[#f5f8fa] rounded-sm flex items-center justify-center flex-shrink-0">
                      <row.icon className="w-4 h-4 text-[#b8893d]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[#34536b] text-xs uppercase tracking-wider">{row.label}</p>
                      <p className="text-[#1B3A5C] font-medium text-sm mt-0.5">{row.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {user?.role === 'user' && (
                <div className="p-6 border-t border-gray-100" data-testid="profil-code-section">
                  <h3 className="text-[#1B3A5C] text-xs tracking-[0.2em] font-semibold mb-1">{t('konto.code_heading')}</h3>
                  <p className="text-[#34536b] text-sm mb-3">{t('konto.code_desc')}</p>
                  <CodeEingeben />
                </div>
              )}
              {user?.role === 'user' && (
                <div className="p-6 border-t border-gray-100" data-testid="profil-benachrichtigungen">
                  <h3 className="text-[#1B3A5C] text-xs tracking-[0.2em] font-semibold mb-3">BENACHRICHTIGUNGEN</h3>
                  <Benachrichtigungen />
                </div>
              )}
              <div className="p-6 border-t border-gray-100" data-testid="profil-anmeldewege">
                <h3 className="text-[#1B3A5C] text-xs tracking-[0.2em] font-semibold mb-3">ANMELDEWEGE</h3>
                <Anmeldewege passwortForm={<PasswordChange />} />
              </div>
              <div className="p-6 border-t border-gray-100">
                <h3 className="text-[#1B3A5C] text-xs tracking-[0.2em] font-semibold mb-3">{t('konto.zweifa_heading')}</h3>
                <ZweiFaktor />
              </div>
              <div className="p-6 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3" data-testid="profil-tour-section">
                <div>
                  <h3 className="text-[#1B3A5C] text-xs tracking-[0.2em] font-semibold">{t('profil.tour.heading')}</h3>
                  <p className="text-[#34536b] text-sm mt-1">{t('profil.tour.desc')}</p>
                </div>
                <Button onClick={() => navigate('/dashboard?tour=1')} variant="outline" className="border-[#b8893d] text-[#1B3A5C] hover:bg-[#b8893d] hover:text-white tracking-wider text-xs" data-testid="profil-tour-restart">
                  <Compass className="mr-2 w-3.5 h-3.5" /> {t('profil.tour.button')}
                </Button>
              </div>

              <div className="p-6 bg-[#fafaf7] border-t border-gray-100">
                <h3 className="text-[#1B3A5C] text-xs tracking-[0.2em] font-semibold mb-3">{t('profil.session.heading')}</h3>
                <Button onClick={handleLogout} variant="outline" className="border-rose-500 text-rose-600 hover:bg-rose-500 hover:text-white tracking-wider text-xs">
                  <LogOut className="mr-2 w-3.5 h-3.5" /> {t('profil.session.logout')}
                </Button>
              </div>
            </div>


            <p className="text-center text-xs text-[#34536b]/60 mt-6">
              {t('profil.contact_note')}
            </p>
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
      <BackToTop />
    </div>
  );
};

export default Profil;
